import { jsx as _jsx } from "react/jsx-runtime";
import { lazy } from 'react';
import { createBrowserRouter } from 'react-router-dom';
const CHUNK_RELOAD_FLAG = 'chunk-reload-attempted';
const lazyRetry = (importFn) => lazy(() => importFn()
    .then((module) => {
    sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
    return module;
})
    .catch((error) => {
    if (sessionStorage.getItem(CHUNK_RELOAD_FLAG) !== '1') {
        sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
        window.location.reload();
        // Never resolves — the page is reloading
        return new Promise(() => { });
    }
    throw error;
}));
import ProtectedRoute from '../auth/ProtectedRoute';
import RequirePermission from '../auth/RequirePermission';
import { PERMISSIONS } from '../auth/permissions';
import AppLayout from '../layout/AppLayout';
import DashboardPage from '../pages/Dashboard/DashboardPage';
const LoginPage = lazy(() => import('../auth/LoginPage'));
const SsoCallbackPage = lazy(() => import('../auth/SsoCallbackPage'));
const ForgotPasswordPage = lazy(() => import('../auth/ForgotPasswordPage'));
const SetNewPasswordPage = lazy(() => import('../auth/SetNewPasswordPage'));
const UnauthorizedPage = lazy(() => import('../pages/unauthorized/UnauthorizedPage'));
const RequestList = lazy(() => import('../pages/requests/RequestList'));
const AnnotateList = lazy(() => import('../pages/annotate/AnnotateList'));
const AnnotationViewer = lazy(() => import('../pages/requests/AnnotationViewer'));
const AnnotationReviewManagement = lazy(() => import('../pages/requests/AnnotationReviewManagement'));
const ReviewAndSubmission = lazy(() => import('../pages/requests/ReviewAndSubmission'));
const CreateWorkflow = lazy(() => import('../pages/requests/CreateWorkflow'));
const CreateRequest = lazy(() => import('../pages/requests/CreateRequest/CreateRequest'));
const AIPlayground = lazy(() => import('../pages/playground/AIPlayground'));
const PlaygroundHistory = lazy(() => import('../pages/playground/PlaygroundHistory'));
const PlaygroundJobViewer = lazy(() => import('../pages/playground/PlaygroundJobViewer'));
const SettingsPage = lazy(() => import('../pages/settings/SettingsPage'));
const BrandForm = lazy(() => import('../pages/settings/BrandForm'));
const CollectionList = lazy(() => import('../pages/settings/CollectionList'));
const CollectionForm = lazy(() => import('../pages/settings/CollectionForm'));
const UserList = lazy(() => import('../pages/settings/UserList'));
const UserManagement = lazy(() => import('../pages/settings/UserManagement/UserManagement'));
const CreateUserPage = lazy(() => import('../pages/settings/UserManagement/CreateUserPage/CreateUserPage'));
const RoleManagement = lazy(() => import('../pages/settings/RoleManagement/RoleManagement'));
const ReviewerGroups = lazy(() => import('../pages/settings/ReviewerGroups/ReviewerGroups'));
const HelpCenterPage = lazy(() => import('../pages/help-center/HelpCenterPage'));
const ProductConfiguration = lazy(() => import('../pages/settings/ProductConfiguration/ProductConfiguration'));
const RequestTypeConfiguration = lazy(() => import('../pages/settings/RequestTypeConfiguration/RequestTypeConfiguration'));
const RequestTypeConfigure = lazy(() => import('../pages/settings/RequestTypeConfiguration/RequestTypeConfigure'));
const MasterData = lazy(() => import('../pages/settings/MasterData/MasterData'));
const AICollectionSetup = lazy(() => import('../pages/settings/AICollectionSetup/AICollectionSetup'));
const AICollectionForm = lazy(() => import('../pages/settings/AICollectionSetup/AICollectionForm'));
const AssetLibrary = lazy(() => import('../pages/asset-library/AssetLibrary'));
const AssetDetailPage = lazy(() => import('../pages/asset/AssetDetailPage'));
const IntegrationQueue = lazy(() => import('../pages/integration-queue/IntegrationQueue'));
const AuditLogs = lazy(() => import('../pages/Audit/AuditLogs'));
export const router = createBrowserRouter([
    { path: '/login', element: _jsx(LoginPage, {}) },
    { path: '/sso-callback', element: _jsx(SsoCallbackPage, {}) },
    { path: '/sso-auth', element: _jsx(SsoCallbackPage, {}) },
    { path: '/forgot-password', element: _jsx(ForgotPasswordPage, {}) },
    { path: '/reset-password', element: _jsx(SetNewPasswordPage, {}) },
    { path: '/set-new-password', element: _jsx(SetNewPasswordPage, {}) },
    {
        path: '/',
        element: _jsx(ProtectedRoute, { children: _jsx(AppLayout, {}) }),
        children: [
            { index: true, element: _jsx(DashboardPage, {}) },
            { path: 'unauthorized', element: _jsx(UnauthorizedPage, {}) },
            { path: 'asset-library', element: _jsx(AssetLibrary, {}) },
            // Request creators need the list route because the Requests menu lands here.
            {
                path: 'requests',
                element: (_jsx(RequirePermission, { permission: [PERMISSIONS.REQUEST_VIEW, PERMISSIONS.REQUEST_CREATE], children: _jsx(RequestList, {}) })),
            },
            {
                path: 'annotate',
                element: (_jsx(RequirePermission, { permission: [PERMISSIONS.REQUEST_REVIEW, PERMISSIONS.REQUEST_CREATE], children: _jsx(AnnotateList, {}) })),
            },
            {
                // Annotation studio — assetId is the version-level id of the asset to open
                path: 'annotate/annotation-studio/:requestId/:assetId',
                element: (_jsx(RequirePermission, { permission: [PERMISSIONS.REQUEST_VIEW, PERMISSIONS.REQUEST_REVIEW], children: _jsx(AnnotationViewer, {}) })),
            },
            {
                path: 'workflow/new',
                element: (_jsx(RequirePermission, { permission: PERMISSIONS.REQUEST_CREATE, children: _jsx(CreateWorkflow, {}) })),
            },
            {
                path: 'requests/create',
                element: (_jsx(RequirePermission, { permission: PERMISSIONS.REQUEST_CREATE, children: _jsx(CreateRequest, {}) })),
            },
            {
                // create_request stays here: the create flow redirects to this URL after saving the draft
                path: 'request/edit/:requestId',
                element: (_jsx(RequirePermission, { permission: [PERMISSIONS.REQUEST_CREATE, PERMISSIONS.REQUEST_EDIT], children: _jsx(CreateRequest, {}) })),
            },
            {
                path: 'annotate/:requestId/review',
                element: (_jsx(RequirePermission, { permission: [PERMISSIONS.REQUEST_VIEW, PERMISSIONS.REQUEST_REVIEW], children: _jsx(AnnotationReviewManagement, {}) })),
            },
            {
                path: 'annotate/:requestId/review-submission',
                element: (_jsx(RequirePermission, { permission: [PERMISSIONS.REQUEST_VIEW, PERMISSIONS.REQUEST_REVIEW], children: _jsx(ReviewAndSubmission, {}) })),
            },
            // AI Playground
            {
                path: 'playground',
                element: (_jsx(RequirePermission, { permission: PERMISSIONS.PLAYGROUND_ACCESS, children: _jsx(AIPlayground, {}) })),
            },
            {
                path: 'playground/history',
                element: (_jsx(RequirePermission, { permission: PERMISSIONS.PLAYGROUND_ACCESS, children: _jsx(PlaygroundHistory, {}) })),
            },
            {
                path: 'playground/job/:jobId',
                element: (_jsx(RequirePermission, { permission: PERMISSIONS.PLAYGROUND_ACCESS, children: _jsx(PlaygroundJobViewer, {}) })),
            },
            // Settings (legacy)
            { path: 'settings', element: _jsx(SettingsPage, {}) },
            { path: 'settings/brand/new', element: _jsx(BrandForm, {}) },
            { path: 'settings/brand/edit/:brandId', element: _jsx(BrandForm, {}) },
            { path: 'settings/collections', element: _jsx(CollectionList, {}) },
            { path: 'settings/collection/new', element: _jsx(CollectionForm, {}) },
            { path: 'settings/collection/edit/:collectionId', element: _jsx(CollectionForm, {}) },
            { path: 'settings/collection/rules/:collectionId', element: _jsx(CollectionForm, {}) },
            { path: 'settings/users', element: _jsx(UserList, {}) },
            // Admin — User & Role Management (super_admin only — no dedicated permission in the list)
            {
                path: 'user-management',
                element: (_jsx(RequirePermission, { permission: PERMISSIONS.SUPER_ADMIN, children: _jsx(UserManagement, {}) })),
            },
            {
                path: 'user-management/create',
                element: (_jsx(RequirePermission, { permission: PERMISSIONS.SUPER_ADMIN, children: _jsx(CreateUserPage, {}) })),
            },
            {
                path: 'user-management/:id/edit',
                element: (_jsx(RequirePermission, { permission: PERMISSIONS.SUPER_ADMIN, children: _jsx(CreateUserPage, {}) })),
            },
            {
                path: 'user-management/:id/view',
                element: (_jsx(RequirePermission, { permission: PERMISSIONS.SUPER_ADMIN, children: _jsx(CreateUserPage, {}) })),
            },
            {
                path: 'role-management',
                element: (_jsx(RequirePermission, { permission: PERMISSIONS.SUPER_ADMIN, children: _jsx(RoleManagement, {}) })),
            },
            {
                path: 'reviewer-groups',
                element: (_jsx(RequirePermission, { permission: PERMISSIONS.SUPER_ADMIN, children: _jsx(ReviewerGroups, {}) })),
            },
            {
                path: 'request-type-configuration',
                element: (_jsx(RequirePermission, { permission: [PERMISSIONS.SUPER_ADMIN, PERMISSIONS.REQUEST_TYPE_CONFIG_VIEW], children: _jsx(RequestTypeConfiguration, {}) })),
            },
            {
                // create/edit included: creating a request type lands on this configure screen
                path: 'request-type-configuration/:id/configure',
                element: (_jsx(RequirePermission, { permission: [
                        PERMISSIONS.SUPER_ADMIN,
                        PERMISSIONS.REQUEST_TYPE_CONFIG_VIEW,
                        PERMISSIONS.REQUEST_TYPE_CONFIG_CREATE,
                        PERMISSIONS.REQUEST_TYPE_CONFIG_EDIT,
                    ], children: _jsx(RequestTypeConfigure, {}) })),
            },
            // Admin — Product Configuration (Edit Config or View Config)
            {
                path: 'product-configuration',
                element: (_jsx(RequirePermission, { permission: [PERMISSIONS.CONFIG_EDIT, PERMISSIONS.CONFIG_VIEW], children: _jsx(ProductConfiguration, {}) })),
            },
            {
                path: 'master-data',
                element: (_jsx(RequirePermission, { permission: [PERMISSIONS.CONFIG_EDIT, PERMISSIONS.CONFIG_VIEW], children: _jsx(MasterData, {}) })),
            },
            {
                path: 'ai-collection-setup',
                element: (_jsx(RequirePermission, { permission: [PERMISSIONS.CONFIG_EDIT, PERMISSIONS.CONFIG_VIEW], children: _jsx(AICollectionSetup, {}) })),
            },
            {
                path: 'ai-collection-setup/:id/edit',
                element: (_jsx(RequirePermission, { permission: [PERMISSIONS.CONFIG_EDIT, PERMISSIONS.CONFIG_VIEW], children: _jsx(AICollectionForm, { mode: "edit" }) })),
            },
            {
                path: 'integration-queue',
                element: (_jsx(RequirePermission, { permission: [PERMISSIONS.SUPER_ADMIN, PERMISSIONS.REQUEST_CREATE], children: _jsx(IntegrationQueue, {}) })),
            },
            {
                path: 'audit',
                element: (_jsx(RequirePermission, { permission: PERMISSIONS.SUPER_ADMIN, children: _jsx(AuditLogs, {}) })),
            },
            { path: 'help-center', element: _jsx(HelpCenterPage, {}) },
            { path: 'asset/:assetId', element: _jsx(AssetDetailPage, {}) },
        ],
    },
]);
